import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  Divider,
  TextField,
  Table,
  TableBody,
  TableCell,
  Modal,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  TablePagination,
} from '@mui/material';
import {
  FileCopy,
  CalendarToday,
  LocationOn,
  FolderSpecial,
  ArrowBack,
  CheckCircle,
  Cancel,
  HourglassEmpty,
  Person,
  Description,
  Business,
  Visibility,
  Comment as CommentIcon,
  History,
  InfoOutlined,
  CloudDownload,
  Edit,
  MoreVert,
} from '@mui/icons-material';


const ViewDocument = () => {
  const { id } = useParams(); 
  const navigate = useNavigate();
  const [documentHistory, setDocumentHistory] = useState([]);
  const [document, setDocument] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [approvalComment, setApprovalComment] = useState('');
  const [rejectionComment, setRejectionComment] = useState('');
  const handleOpenModal = () => setIsModalOpen(true);
  const [historyPage, setHistoryPage] = useState(0);
  const [historyRowsPerPage, setHistoryRowsPerPage] = useState(5);
  const handleCloseModal = () => setIsModalOpen(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [filteredComments, setFilteredComments] = useState([]);
  const [isRejectModalOpen, setIsRejectModalOpen] = useState(false);
  const [isApproveModalOpen, setIsApproveModalOpen] = useState(false);
  const [username, setUsername] = useState('');
  const [documentId, setDocumentId] = useState(null);
  const [userId, setUserId] = useState('');
  const commentsPerPage = 5;
  const [loading, setLoading] = useState(false);
  const [approvalStatus, setApprovalStatus] = useState(document?.approval_status || 'new');

  useEffect(() => {
    const fetchDocument = async () => {
      try {
        const response = await fetch(`https://apidashboard.eurekagroup.id/documents/get/${id}`);
        if (response.ok) {
          const data = await response.json();
          setDocument(data);
        } else {
          Swal.fire('Error', 'Failed to fetch document details', 'error');
        }
      } catch (error) {
        console.error('Error fetching document:', error);
        Swal.fire('Error', 'There was an error loading the document', 'error');
      }
    };

    fetchDocument();
  }, [id]);

  useEffect(() => {
    // Get token from local storage
    const token = localStorage.getItem('token'); // Adjust this according to your token key
    if (token) {
      try {
        const parsedToken = JSON.parse(atob(token.split('.')[1])); // Decode JWT to get payload
        setUsername(parsedToken.username); // Set username
        setUserId(parsedToken.userId); // Set userId
      } catch (error) {
        console.error('Failed to parse token:', error);
      }
    }
  }, []);

  useEffect(() => {
    const fetchContacts = async () => {
      try {
        const [employeesData, customersData, vendorsData] = await Promise.all([
          fetch('https://apidashboard.eurekagroup.id/employee').then((res) => res.json()),
          fetch('https://apidashboard.eurekagroup.id/customers').then((res) => res.json()),
          fetch('https://apidashboard.eurekagroup.id/vendor').then((res) => res.json()),
        ]);

        setEmployees(employeesData);
        setCustomers(customersData);
        setVendors(vendorsData);
      } catch (error) {
        console.error('Error fetching contacts:', error);
      }
    };

    fetchContacts();
  }, []);

  useEffect(() => {
    const fetchComments = async () => {
      try {
        const response = await fetch(`https://apidashboard.eurekagroup.id/comments/${id}`);
        if (response.ok) {
          const data = await response.json();
          setComments(data);
        } else {
          Swal.fire('Error', 'Failed to fetch comments', 'error');
        }
      } catch (error) {
        console.error('Error fetching comments:', error);
      }
    };

    fetchComments();
  }, [id]);

  useEffect(() => {
    const fetchDocumentHistory = async () => {
      try {
        const response = await fetch(`https://apidashboard.eurekagroup.id/document-history/${id}`);
        if (response.ok) {
          const data = await response.json();
          setDocumentHistory(data); // Simpan data ke state
        } else {
          Swal.fire('Error', 'Failed to fetch document history', 'error');
        }
      } catch (error) {
        console.error('Error fetching document history:', error);
      }
    };
  
    fetchDocumentHistory();
  }, [id]);

  // Function to update approval status
  const updateApprovalStatus = async (status, comment) => {
    const payload = {
      document_id: id,
      status: status,
      user_id: userId,
      comment: comment,
    };

    try {
      const response = await fetch(
        `https://apidashboard.eurekagroup.id/documents/update-status`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(payload),
        }
      );

      if (response.ok) {
        const updatedDocument = await response.json();
        setDocument((prevDoc) => ({ ...prevDoc, ...updatedDocument }));
        Swal.fire({
          icon: 'success',
          title: status === 'approved' ? 'Approved' : 'Rejected',
          text: `Document has been ${status}.`,
          timer: 2000,
          showConfirmButton: false,
        });
      } else {
        const error = await response.json();
        Swal.fire('Error', error.message || 'Failed to update status', 'error');
      }
    } catch (error) {
      console.error('Error updating approval status:', error);
      Swal.fire('Error', 'An unexpected error occurred', 'error');
    }
  };

  const handleApprove = () => {
    updateApprovalStatus('approved', approvalComment);
    setIsApproveModalOpen(false); // Close the approve modal
  };

  const handleReject = () => {
    updateApprovalStatus('rejected', rejectionComment);
    setIsRejectModalOpen(false); // Close the reject modal
  };

  const handleViewDocument = (file) => {
    const documentUrl = `https://apidashboard.eurekagroup.id/documents/view/${file}`;
    window.open(documentUrl, '_blank');
  };

  const handleBack = () => {
    navigate('/document');
  };

  const getContactName = (idContact) => {
    const contactId = idContact ? idContact._id : idContact;

    const employee = employees.find((emp) => emp._id === contactId);
    if (employee) return employee.full_name;

    const customer = customers.find((cust) => cust._id === contactId);
    if (customer) return customer.customer_name;

    const vendor = vendors.find((vend) => vend._id === contactId);
    if (vendor) return vendor?.vendor_name;

    return '-';
  };

  const handleAddComment = async () => {
    if (!id) {
      console.error('Document ID is not available');
      return;
    }

    if (!userId) {
      console.error('Employee ID is not available');
      return;
    }

    const newCommentData = {
      document_id: id, // Use the id extracted from the URL
      comment: newComment, // The content of the comment
      id_employee: userId, // The employee ID of the user making the comment
    };

    setLoading(true); // Show loading spinner

    try {
      const response = await fetch('https://apidashboard.eurekagroup.id/comments/add', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(newCommentData),
      });

      if (response.ok) {
        const result = await response.json();
        const updatedComment = {
          ...result,
          id_employee: { _id: userId, full_name: "Samuel Sukarno" },
        };

        setComments((prevComments) => [...prevComments, updatedComment]); // Add the new comment to the list
        setNewComment('');  // Reset textarea after submit

        Swal.fire({
          icon: 'success',
          title: 'Comment Added',
          text: 'Your comment has been successfully added!',
          timer: 2000,
          showConfirmButton: false,
        });
      } else {
        console.error('Failed to add comment');
        Swal.fire('Error', 'Failed to add comment', 'error');
      }
    } catch (error) {
      console.error('Error:', error);
      Swal.fire('Error', 'An error occurred while adding your comment', 'error');
    } finally {
      setLoading(false); // Hide loading spinner after request is done
    }
  };

  // Menghitung komentar yang ditampilkan berdasarkan pagination
  useEffect(() => {
    const startIndex = currentPage * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;
    setFilteredComments(comments.slice(startIndex, endIndex));
  }, [comments, currentPage, rowsPerPage]);

  // Handle page changes
  const handlePageChange = (event, newPage) => {
    setCurrentPage(newPage);
  };

  // Handle rows per page change
  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setCurrentPage(0); // Reset to first page when rows per page change
  };

  // Calculate indices for slicing comments array based on pagination
  const indexOfLastComment = currentPage * rowsPerPage;
  const indexOfFirstComment = indexOfLastComment - rowsPerPage;
  const currentComments = comments.slice(indexOfFirstComment, indexOfLastComment);

  // Total number of pages
  const totalPages = Math.ceil(comments.length / rowsPerPage);

  if (!document) {
    return <Typography>Loading...</Typography>;
  }

  // Handle page change for Document History
const handleHistoryPageChange = (event, newPage) => {
  setHistoryPage(newPage);
};

// Handle rows per page change for Document History
const handleHistoryRowsPerPageChange = (event) => {
  setHistoryRowsPerPage(parseInt(event.target.value, 10));
  setHistoryPage(0); // Reset to first page when rows per page change
};

// Calculate indices for slicing history array based on pagination
const indexOfLastHistory = (historyPage + 1) * historyRowsPerPage;
const indexOfFirstHistory = indexOfLastHistory - historyRowsPerPage;
const currentHistory = documentHistory.slice(indexOfFirstHistory, indexOfLastHistory);

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: '#fafbfc' }}>
      {/* Hero Header Section */}
      <Box 
        sx={{ 
          background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)',
          padding: 4,
          marginBottom: 4,
          boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
        }}
      >
        <Box sx={{ maxWidth: 1400, margin: '0 auto' }}>
          <Button
            onClick={handleBack}
            sx={{
              color: 'white',
              marginBottom: 2,
              '&:hover': {
                backgroundColor: 'rgba(255,255,255,0.1)',
              },
            }}
            startIcon={<ArrowBack />}
          >
            Back to Documents
          </Button>
          
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="h3" sx={{ fontWeight: 700, color: 'white', marginBottom: 1 }}>
                {document.document_name}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                <Box sx={{ 
                  backgroundColor: 'rgba(255,255,255,0.2)', 
                  paddingX: 2, 
                  paddingY: 0.5, 
                  borderRadius: 2,
                  backdropFilter: 'blur(10px)',
                }}>
                </Box>
                <Box sx={{ 
                  backgroundColor: document.approval_status === 'approved' 
                    ? 'rgba(76, 175, 80, 0.9)' 
                    : document.approval_status === 'rejected'
                    ? 'rgba(244, 67, 54, 0.9)'
                    : 'rgba(255, 193, 7, 0.9)',
                  paddingX: 2.5, 
                  paddingY: 0.8, 
                  borderRadius: 20,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                }}>
                  {document.approval_status === 'approved' && <CheckCircle sx={{ fontSize: 18 }} />}
                  {document.approval_status === 'rejected' && <Cancel sx={{ fontSize: 18 }} />}
                  {document.approval_status === 'new' && <HourglassEmpty sx={{ fontSize: 18 }} />}
                  <Typography variant="body2" sx={{ color: 'white', fontWeight: 600 }}>
                    {document.approval_status === 'approved' && 'Approved'}
                    {document.approval_status === 'rejected' && 'Rejected'}
                    {document.approval_status === 'new' && 'Pending Review'}
                  </Typography>
                </Box>
              </Box>
            </Box>
            
          </Box>
        </Box>
      </Box>

      <Box sx={{ maxWidth: 1400, margin: '0 auto', padding: 3 }}>
        <Grid container spacing={3}>
          {/* Left Column - Main Info */}
          <Grid item xs={12} lg={8}>

          {/* Document Information Card */}
          <Card 
            sx={{ 
              marginBottom: 3,
              borderRadius: 2,
              border: 'none',
              boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
            }}
          >
            <CardContent sx={{ padding: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, marginBottom: 3 }}>
                <InfoOutlined sx={{ color: '#1976d2', fontSize: 24 }} />
                <Typography variant="h6" sx={{ fontWeight: 700, color: '#1a237e' }}>
                  Document Information
                </Typography>
              </Box>

              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <Box>
                    <Typography variant="caption" sx={{ color: '#78909c', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                      Category
                    </Typography>
                    <Typography variant="body1" sx={{ color: '#263238', fontWeight: 500, marginTop: 0.5 }}>
                      {document.id_document_category?.name}
                    </Typography>
                  </Box>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Box>
                    <Typography variant="caption" sx={{ color: '#78909c', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                      Uploaded By
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, marginTop: 0.5 }}>
                      <Person sx={{ fontSize: 18, color: '#1976d2' }} />
                      <Typography variant="body1" sx={{ color: '#263238', fontWeight: 500 }}>
                        {document.id_user.full_name}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Box>
                    <Typography variant="caption" sx={{ color: '#78909c', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                      Contact
                    </Typography>
                    <Typography variant="body1" sx={{ color: '#263238', fontWeight: 500, marginTop: 0.5 }}>
                      {document.id_contact ? getContactName(document.id_contact) : '-'}
                    </Typography>
                  </Box>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Box>
                    <Typography variant="caption" sx={{ color: '#78909c', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                      Office
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, marginTop: 0.5 }}>
                      <Business sx={{ fontSize: 18, color: '#1976d2' }} />
                      <Typography variant="body1" sx={{ color: '#263238', fontWeight: 500 }}>
                        {document.id_office?.office_name}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Box>
                    <Typography variant="caption" sx={{ color: '#78909c', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                      Cabinet
                    </Typography>
                    <Typography variant="body1" sx={{ color: '#263238', fontWeight: 500, marginTop: 0.5 }}>
                      {document.id_cabinet?.cabinet_name}
                    </Typography>
                  </Box>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Box>
                    <Typography variant="caption" sx={{ color: '#78909c', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                      PIC Name
                    </Typography>
                    <Typography variant="body1" sx={{ color: '#263238', fontWeight: 500, marginTop: 0.5 }}>
                      {document.pic_name}
                    </Typography>
                  </Box>
                </Grid>

                <Grid item xs={12}>
                  <Divider sx={{ marginY: 1 }} />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Box>
                    <Typography variant="caption" sx={{ color: '#78909c', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                      Start Date
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, marginTop: 0.5 }}>
                      <CalendarToday sx={{ fontSize: 16, color: '#1976d2' }} />
                      <Typography variant="body1" sx={{ color: '#263238', fontWeight: 500 }}>
                        {new Date(document.start_date).toLocaleDateString()}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Box>
                    <Typography variant="caption" sx={{ color: '#78909c', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                      End Date
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, marginTop: 0.5 }}>
                      <CalendarToday sx={{ fontSize: 16, color: '#1976d2' }} />
                      <Typography variant="body1" sx={{ color: '#263238', fontWeight: 500 }}>
                        {document.end_date ? new Date(document.end_date).toLocaleDateString() : 'Seumur Hidup'}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Box>
                    <Typography variant="caption" sx={{ color: '#78909c', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                      Date Added
                    </Typography>
                    <Typography variant="body1" sx={{ color: '#263238', fontWeight: 500, marginTop: 0.5 }}>
                      {new Date(document.date_added).toLocaleDateString()}
                    </Typography>
                  </Box>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Box>
                    <Typography variant="caption" sx={{ color: '#78909c', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                      Last Modified
                    </Typography>
                    <Typography variant="body1" sx={{ color: '#263238', fontWeight: 500, marginTop: 0.5 }}>
                      {new Date(document.date_modified).toLocaleDateString()}
                    </Typography>
                  </Box>
                </Grid>

                {document.note && (
                  <>
                    <Grid item xs={12}>
                      <Divider sx={{ marginY: 1 }} />
                    </Grid>
                    <Grid item xs={12}>
                      <Box>
                        <Typography variant="caption" sx={{ color: '#78909c', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                          Notes
                        </Typography>
                        <Box 
                          sx={{ 
                            backgroundColor: '#e3f2fd', 
                            padding: 2, 
                            borderRadius: 1, 
                            marginTop: 1,
                            borderLeft: '4px solid #1976d2'
                          }}
                        >
                          <Typography variant="body2" sx={{ color: '#263238' }}>
                            {document.note}
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>
                  </>
                )}
              </Grid>
            </CardContent>
          </Card>

          {/* Comments Section */}
          <Card 
            sx={{ 
              marginBottom: 3,
              borderRadius: 2,
              border: 'none',
              boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
            }}
          >
            <CardContent sx={{ padding: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, marginBottom: 3 }}>
                <CommentIcon sx={{ color: '#1976d2', fontSize: 24 }} />
                <Typography variant="h6" sx={{ fontWeight: 700, color: '#1a237e' }}>
                  Comments ({comments.length})
                </Typography>
              </Box>

              {/* Add Comment */}
              <Box sx={{ marginBottom: 3 }}>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  placeholder="Add your comment here..."
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: '#fafafa',
                      '&:hover fieldset': {
                        borderColor: '#1976d2',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#1976d2',
                      },
                    },
                  }}
                />
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', marginTop: 1 }}>
                  <Button
                    variant="contained"
                    onClick={handleAddComment}
                    disabled={loading || !newComment.trim()}
                    sx={{
                      backgroundColor: '#1976d2',
                      '&:hover': {
                        backgroundColor: '#1565c0',
                      },
                    }}
                  >
                    {loading ? <CircularProgress size={24} sx={{ color: 'white' }} /> : 'Post Comment'}
                  </Button>
                </Box>
              </Box>

              {/* Comments List */}
              <Box sx={{ maxHeight: 400, overflowY: 'auto' }}>
                {filteredComments.length > 0 ? (
                  filteredComments.map((comment, index) => (
                    <Box
                      key={index}
                      sx={{
                        padding: 2,
                        marginBottom: 2,
                        backgroundColor: '#fafafa',
                        borderRadius: 1,
                        borderLeft: '3px solid #1976d2',
                      }}
                    >
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', marginBottom: 1 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#1a237e' }}>
                          {comment.id_employee?.full_name || "Unknown User"}
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#78909c' }}>
                          {new Date(comment.tanggal_comment).toLocaleString()}
                        </Typography>
                      </Box>
                      <Typography variant="body2" sx={{ color: '#263238' }}>
                        {comment.comment}
                      </Typography>
                    </Box>
                  ))
                ) : (
                  <Box sx={{ textAlign: 'center', padding: 4, color: '#78909c' }}>
                    <CommentIcon sx={{ fontSize: 48, opacity: 0.3, marginBottom: 1 }} />
                    <Typography variant="body2">No comments yet. Be the first to comment!</Typography>
                  </Box>
                )}
              </Box>

              {comments.length > rowsPerPage && (
                <TablePagination
                  rowsPerPageOptions={[5, 10, 25]}
                  component="div"
                  count={comments.length}
                  rowsPerPage={rowsPerPage}
                  page={currentPage}
                  onPageChange={handlePageChange}
                  onRowsPerPageChange={handleRowsPerPageChange}
                  sx={{ borderTop: '1px solid #e0e0e0', marginTop: 2 }}
                />
              )}
            </CardContent>
          </Card>

          {/* Document History */}
          <Card 
            sx={{ 
              borderRadius: 2,
              border: 'none',
              boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
            }}
          >
            <CardContent sx={{ padding: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, marginBottom: 3 }}>
                <History sx={{ color: '#1976d2', fontSize: 24 }} />
                <Typography variant="h6" sx={{ fontWeight: 700, color: '#1a237e' }}>
                  Document History
                </Typography>
              </Box>

              {currentHistory.length > 0 ? (
                <>
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow sx={{ backgroundColor: '#fafafa' }}>
                          <TableCell sx={{ fontWeight: 600, color: '#1a237e' }}>No</TableCell>
                          <TableCell sx={{ fontWeight: 600, color: '#1a237e' }}>Uploaded By</TableCell>
                          <TableCell sx={{ fontWeight: 600, color: '#1a237e' }}>Previous Filename</TableCell>
                          <TableCell sx={{ fontWeight: 600, color: '#1a237e' }}>Upload Date</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {currentHistory.map((history, index) => (
                          <TableRow 
                            key={index}
                            sx={{
                              '&:hover': {
                                backgroundColor: '#f5f5f5',
                              },
                            }}
                          >
                            <TableCell>{historyPage * historyRowsPerPage + index + 1}</TableCell>
                            <TableCell sx={{ fontWeight: 500 }}>{history.id_employee?.full_name || "Unknown"}</TableCell>
                            <TableCell sx={{ color: '#546e7a', fontSize: '0.875rem' }}>{history.previous_file_name || "N/A"}</TableCell>
                            <TableCell sx={{ color: '#78909c' }}>
                              {history.date_uploaded ? new Date(history.date_uploaded).toLocaleString() : "N/A"}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  <TablePagination
                    rowsPerPageOptions={[5, 10, 25]}
                    component="div"
                    count={documentHistory.length}
                    rowsPerPage={historyRowsPerPage}
                    page={historyPage}
                    onPageChange={handleHistoryPageChange}
                    onRowsPerPageChange={handleHistoryRowsPerPageChange}
                    sx={{ borderTop: '1px solid #e0e0e0', marginTop: 2 }}
                  />
                </>
              ) : (
                <Box sx={{ textAlign: 'center', padding: 4, color: '#78909c' }}>
                  <History sx={{ fontSize: 48, opacity: 0.3, marginBottom: 1 }} />
                  <Typography variant="body2">No history available</Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Right Column - File Info & Actions */}
        <Grid item xs={12} lg={4}>
          {/* File Preview Card */}
          <Card 
            sx={{ 
              marginBottom: 3,
              borderRadius: 2,
              border: 'none',
              boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
            }}
          >
            <CardContent sx={{ padding: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, marginBottom: 3 }}>
                <Description sx={{ color: '#1976d2', fontSize: 24 }} />
                <Typography variant="h6" sx={{ fontWeight: 700, color: '#1a237e' }}>
                  File Details
                </Typography>
              </Box>

              <Box 
                sx={{ 
                  backgroundColor: '#e3f2fd',
                  borderRadius: 2,
                  padding: 3,
                  textAlign: 'center',
                  marginBottom: 3,
                }}
              >
                <FileCopy sx={{ fontSize: 64, color: '#1976d2', marginBottom: 2 }} />
                <Typography variant="body2" sx={{ color: '#263238', wordBreak: 'break-word', fontWeight: 500 }}>
                  {document.file_name}
                </Typography>
              </Box>

              <Button
                fullWidth
                variant="contained"
                size="large"
                onClick={() => handleViewDocument(document.file_name)}
                sx={{
                  backgroundColor: '#1976d2',
                  marginBottom: 1.5,
                  padding: 1.5,
                  '&:hover': {
                    backgroundColor: '#1565c0',
                  },
                }}
                startIcon={<Visibility />}
              >
                View Document
              </Button>

              <Button
                fullWidth
                variant="outlined"
                size="large"
                onClick={() => handleViewDocument(document.file_name)}
                sx={{
                  borderColor: '#1976d2',
                  color: '#1976d2',
                  padding: 1.5,
                  '&:hover': {
                    borderColor: '#1565c0',
                    backgroundColor: '#e3f2fd',
                  },
                }}
                startIcon={<CloudDownload />}
              >
                Download
              </Button>
            </CardContent>
          </Card>

          {/* Quick Info Card */}
          <Card 
            sx={{ 
              borderRadius: 2,
              border: 'none',
              boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
            }}
          >
            <CardContent sx={{ padding: 3 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#1a237e', marginBottom: 2 }}>
                Quick Information
              </Typography>

              <Box sx={{ marginBottom: 2 }}>
                <Typography variant="caption" sx={{ color: '#78909c', display: 'block', marginBottom: 0.5 }}>
                  Document ID
                </Typography>
                <Typography variant="body2" sx={{ color: '#263238', fontWeight: 500, fontFamily: 'monospace' }}>
                  {id}
                </Typography>
              </Box>

              <Box sx={{ marginBottom: 2 }}>
                <Typography variant="caption" sx={{ color: '#78909c', display: 'block', marginBottom: 0.5 }}>
                  Total Comments
                </Typography>
                <Typography variant="body2" sx={{ color: '#263238', fontWeight: 500 }}>
                  {comments.length} comment{comments.length !== 1 ? 's' : ''}
                </Typography>
              </Box>

              <Box>
                <Typography variant="caption" sx={{ color: '#78909c', display: 'block', marginBottom: 0.5 }}>
                  History Records
                </Typography>
                <Typography variant="body2" sx={{ color: '#263238', fontWeight: 500 }}>
                  {documentHistory.length} record{documentHistory.length !== 1 ? 's' : ''}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Approve Modal */}
      <Modal
        open={isApproveModalOpen}
        onClose={() => setIsApproveModalOpen(false)}
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 450,
            bgcolor: 'background.paper',
            borderRadius: 3,
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
            p: 4,
          }}
        >
          <Typography variant="h5" sx={{ fontWeight: 700, color: '#1a237e', marginBottom: 3 }}>
            Approve Document
          </Typography>
          <TextField
            label="Approval Comment"
            placeholder="Add your approval comment (optional)"
            multiline
            rows={4}
            fullWidth
            value={approvalComment}
            onChange={(e) => setApprovalComment(e.target.value)}
            sx={{
              marginBottom: 3,
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
              },
            }}
          />
          <Box display="flex" gap={2} justifyContent="flex-end">
            <Button 
              variant="outlined" 
              onClick={() => setIsApproveModalOpen(false)}
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                paddingX: 3,
                paddingY: 1,
              }}
            >
              Cancel
            </Button>
            <Button 
              variant="contained" 
              onClick={handleApprove}
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                paddingX: 3,
                paddingY: 1,
                backgroundColor: '#4caf50',
                '&:hover': {
                  backgroundColor: '#43a047',
                },
              }}
            >
              Approve Document
            </Button>
          </Box>
        </Box>
      </Modal>

      {/* Reject Modal */}
      <Modal
        open={isRejectModalOpen}
        onClose={() => setIsRejectModalOpen(false)}
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 450,
            bgcolor: 'background.paper',
            borderRadius: 3,
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
            p: 4,
          }}
        >
          <Typography variant="h5" sx={{ fontWeight: 700, color: '#1a237e', marginBottom: 3 }}>
            Reject Document
          </Typography>
          <TextField
            label="Rejection Comment"
            placeholder="Please provide a reason for rejection"
            multiline
            rows={4}
            fullWidth
            value={rejectionComment}
            onChange={(e) => setRejectionComment(e.target.value)}
            sx={{
              marginBottom: 3,
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
              },
            }}
          />
          <Box display="flex" gap={2} justifyContent="flex-end">
            <Button 
              variant="outlined" 
              onClick={() => setIsRejectModalOpen(false)}
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                paddingX: 3,
                paddingY: 1,
              }}
            >
              Cancel
            </Button>
            <Button 
              variant="contained" 
              onClick={handleReject}
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                paddingX: 3,
                paddingY: 1,
                backgroundColor: '#f44336',
                '&:hover': {
                  backgroundColor: '#e53935',
                },
              }}
            >
              Reject Document
            </Button>
          </Box>
        </Box>
      </Modal>
      </Box>
    </Box>
  );
};

export default ViewDocument;